// Statutory Holiday Pack governance (statutory-holiday program Phase 2). The
// gazetted public-holiday calendar per jurisdiction, versioned and published
// under the same maker-checker discipline as tax country packs. Publishing fans
// the entries out to every subscribed tenant's leave calendar (statutory rows
// there become read-only). Same-day effective dates are allowed with a
// mandatory reason (emergency gazette declarations — D5).
import { fmtDate, fmtDateTime } from '../../lib/format';
import React, { useEffect, useMemo, useState } from 'react';
import {
  Table, Tag, Button, Drawer, Typography, Space, Alert, Descriptions, Empty, message, Card,
  Modal, Form, Input, DatePicker, Select, Checkbox, Tabs, Popconfirm,
} from 'antd';
import { ReloadOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import type { ColumnsType } from 'antd/es/table';
import { PageHeader, StatusTag } from '../../components/console';
import { useAppSelector } from '../../app/hooks';
import { selectRoles } from '../../features/auth/authSlice';
import {
  listHolidayPacks, getHolidayPack, createHolidayPackDraft, updateHolidayPackDraft,
  submitHolidayPack, publishHolidayPack, rejectHolidayPack, discardHolidayPack,
  listHolidayPackSubscriptions,
  type HolidayPack, type HolidayPackEntry, type HolidayPackSubscription,
} from '../../services/holidayPackService';

const { Text } = Typography;

const AUTHOR_ROLES = ['tax_officer', 'platform_admin', 'platform_owner', 'super_admin', 'system_admin'];
const PUBLISH_ROLES = ['platform_admin', 'platform_owner', 'super_admin', 'system_admin'];

const statusView = (s: string): { status: string; label: string } => {
  if (s === 'published') return { status: 'active', label: 'Published' };
  if (s === 'draft') return { status: 'draft', label: 'Draft' };
  if (s === 'in_review') return { status: 'review', label: 'In review' };
  return { status: 'inactive', label: 'Superseded' };
};


const KIND_COLOR: Record<string, string> = { fixed: 'blue', movable: 'gold', declared: 'red' };

export function HolidayPacksPage() {
  const [packs, setPacks] = useState<HolidayPack[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [detail, setDetail] = useState<HolidayPack | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [editing, setEditing] = useState(false); // detail drawer in edit mode
  const [entries, setEntries] = useState<HolidayPackEntry[]>([]);
  const [saving, setSaving] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [subs, setSubs] = useState<HolidayPackSubscription[] | null>(null);
  const [createForm] = Form.useForm();
  const [publishForm] = Form.useForm();
  const [publishFor, setPublishFor] = useState<HolidayPack | null>(null);

  const roles = useAppSelector(selectRoles);
  const canAuthor = roles.some((r) => AUTHOR_ROLES.includes(r));
  const canPublish = roles.some((r) => PUBLISH_ROLES.includes(r));

  const load = async (initial = false) => {
    if (initial) setLoading(true);
    else setRefreshing(true);
    try {
      setPacks(await listHolidayPacks());
    } catch {
      if (initial) message.error('Could not load holiday packs');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };
  useEffect(() => { void load(true); /* eslint-disable-next-line */ }, []);

  const loadSubs = async () => {
    try {
      setSubs(await listHolidayPackSubscriptions());
    } catch {
      setSubs([]);
      message.error('Could not load tenant subscriptions');
    }
  };

  const openDetail = async (id: string, edit = false) => {
    setDetailLoading(true);
    setEditing(edit);
    setDetail({ id } as HolidayPack);
    try {
      const p = await getHolidayPack(id);
      setDetail(p);
      setEntries(p.entries ?? []);
    } catch {
      message.error('Could not load pack');
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const apiErr = (e: any, fallback: string) =>
    message.error(e?.response?.data?.message || e?.response?.data?.error || fallback);

  const saveDraft = async () => {
    if (!detail) return;
    setSaving(true);
    try {
      await updateHolidayPackDraft(detail.id, { name: detail.name, entries });
      message.success('Draft saved');
      await load(false);
      await openDetail(detail.id, true);
    } catch (e: any) {
      apiErr(e, 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const act = async (fn: () => Promise<void>, okMsg: string) => {
    setSaving(true);
    try {
      await fn();
      message.success(okMsg);
      setDetail(null);
      setPublishFor(null);
      await load(false);
    } catch (e: any) {
      apiErr(e, 'Action failed');
    } finally {
      setSaving(false);
    }
  };

  const byCountry = useMemo(() => {
    const m = new Map<string, HolidayPack[]>();
    for (const p of packs) {
      const arr = m.get(p.country_code) ?? [];
      arr.push(p);
      m.set(p.country_code, arr);
    }
    return Array.from(m.entries());
  }, [packs]);

  const versionColumns: ColumnsType<HolidayPack> = [
    { title: 'Version', dataIndex: 'version', width: 90, render: (v: number) => <Text strong>v{v}</Text> },
    { title: 'Name', dataIndex: 'name' },
    { title: 'Effective', width: 200, render: (_: unknown, p) => `${fmtDate(p.effective_from)} → ${p.effective_to ? fmtDate(p.effective_to) : 'open'}` },
    { title: 'Source', dataIndex: 'source', width: 90, render: (s: string) => <Tag>{s}</Tag> },
    {
      title: 'Status', width: 120,
      render: (_: unknown, p) => { const v = statusView(p.status); return <StatusTag status={v.status} label={v.label} />; },
    },
    {
      title: '', width: 240, align: 'right' as const,
      render: (_: unknown, p) => (
        <Space size={4}>
          {canAuthor && p.status === 'draft' && (
            <Button size="small" type="link" onClick={() => void openDetail(p.id, true)}>Edit</Button>
          )}
          {p.status === 'in_review' && (
            <Button size="small" type="link" onClick={() => void openDetail(p.id, false)}>
              {canPublish ? 'Review & publish' : 'Review'}
            </Button>
          )}
          <Button size="small" type="link" onClick={() => void openDetail(p.id, false)}>View</Button>
        </Space>
      ),
    },
  ];

  const entryColumns = (editable: boolean): ColumnsType<HolidayPackEntry> => [
    {
      title: 'Date', dataIndex: 'holiday_date', width: 140,
      render: (v: string, _e, i) => editable ? (
        <DatePicker
          size="small" value={v ? dayjs(v) : null} allowClear={false}
          onChange={(d) => setEntries((prev) => prev.map((x, j) => (j === i ? { ...x, holiday_date: d!.format('YYYY-MM-DD') } : x)))}
        />
      ) : v,
    },
    {
      title: 'Name', dataIndex: 'name',
      render: (v: string, _e, i) => editable ? (
        <Input size="small" value={v}
          onChange={(ev) => setEntries((prev) => prev.map((x, j) => (j === i ? { ...x, name: ev.target.value } : x)))} />
      ) : v,
    },
    {
      title: 'Kind', dataIndex: 'kind', width: 120,
      render: (v: string, _e, i) => editable ? (
        <Select
          size="small" value={v} style={{ width: 110 }}
          options={['fixed', 'movable', 'declared'].map((k) => ({ value: k, label: k }))}
          onChange={(k) => setEntries((prev) => prev.map((x, j) => (j === i ? { ...x, kind: k as HolidayPackEntry['kind'] } : x)))}
        />
      ) : <Tag color={KIND_COLOR[v]}>{v}</Tag>,
    },
    {
      title: 'Observed', dataIndex: 'observed_date', width: 140,
      render: (v: string | null, _e, i) => editable ? (
        <DatePicker
          size="small" value={v ? dayjs(v) : null} placeholder="same day"
          onChange={(d) => setEntries((prev) => prev.map((x, j) => (j === i ? { ...x, observed_date: d ? d.format('YYYY-MM-DD') : null } : x)))}
        />
      ) : (v ?? '—'),
    },
    {
      title: 'Half day', dataIndex: 'half_day', width: 90, align: 'center' as const,
      render: (v: boolean, _e, i) => editable ? (
        <Checkbox checked={v}
          onChange={(ev) => setEntries((prev) => prev.map((x, j) => (j === i ? { ...x, half_day: ev.target.checked } : x)))} />
      ) : (v ? 'Yes' : '—'),
    },
    ...(editable ? [{
      title: '', width: 50,
      render: (_: unknown, _e: HolidayPackEntry, i: number) => (
        <Button size="small" type="text" danger icon={<DeleteOutlined />}
          onClick={() => setEntries((prev) => prev.filter((_x, j) => j !== i))} />
      ),
    }] : []),
  ];

  const subColumns: ColumnsType<HolidayPackSubscription> = [
    { title: 'Tenant', dataIndex: 'tenant_id', render: (v: string) => <Text code>{v}</Text> },
    { title: 'Country', dataIndex: 'country_code', width: 100 },
    {
      title: 'Jurisdiction', width: 160,
      render: (_: unknown, s) => s.locked
        ? <Tag color="blue">Mandatory (registration)</Tag>
        : <Tag>Additional (inert)</Tag>,
    },
    { title: 'Auto-update', dataIndex: 'auto', width: 110, render: (v: boolean) => (v ? 'Yes' : 'No') },
    { title: 'Since', dataIndex: 'created_at', width: 130, render: (v?: string | null) => fmtDate(v) },
  ];

  const packsTab = loading ? (
    <Card loading />
  ) : byCountry.length === 0 ? (
    <Empty description="No holiday packs." />
  ) : (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      {byCountry.map(([country, versions]) => {
        const active = versions.find((v) => v.status === 'published');
        const hasOpenDraft = versions.some((v) => v.status === 'draft' || v.status === 'in_review');
        return (
          <Card
            key={country}
            title={<Space><Text strong>{country}</Text><Text type="secondary">{active?.name}</Text></Space>}
            extra={canAuthor && !hasOpenDraft && (
              <Button size="small" icon={<PlusOutlined />}
                onClick={() => { createForm.setFieldsValue({ country_code: country, clone_from_active: true }); setCreateOpen(true); }}>
                New version
              </Button>
            )}
          >
            <Table rowKey="id" size="small" pagination={false} dataSource={versions} columns={versionColumns} />
          </Card>
        );
      })}
    </Space>
  );

  return (
    <div style={{ padding: 24 }}>
      <PageHeader
        title="Holiday packs"
        subtitle="Gazetted public-holiday calendars per jurisdiction, versioned and published to tenant leave calendars"
        actions={
          <Space>
            {canAuthor && (
              <Button icon={<PlusOutlined />} onClick={() => { createForm.resetFields(); setCreateOpen(true); }}>
                New country
              </Button>
            )}
            <Button icon={<ReloadOutlined spin={refreshing} />} onClick={() => void load(false)} disabled={refreshing}>
              Refresh
            </Button>
          </Space>
        }
      />

      <Alert
        type="info" showIcon style={{ marginBottom: 16 }}
        message="Governed statutory calendar"
        description="Each tenant's registration country is subscribed automatically and locked — statutory rows in their calendar are read-only. Corrections (a moved Eid, a newly declared holiday) are published as a new pack version: same-day effective dates are allowed but a change reason is mandatory, and a second platform admin must publish."
      />

      <Tabs
        items={[
          { key: 'packs', label: 'Packs', children: packsTab },
          {
            key: 'subs', label: 'Subscribing tenants',
            children: subs === null ? <Card loading /> : (
              <Table rowKey={(s) => `${s.tenant_id}:${s.country_code}`} size="small"
                dataSource={subs} columns={subColumns} pagination={{ pageSize: 25 }} />
            ),
          },
        ]}
        onChange={(k) => { if (k === 'subs' && subs === null) void loadSubs(); }}
      />

      {/* Detail / editor drawer */}
      <Drawer
        title={detail ? `${detail.country_code ?? ''} · ${detail.name ?? ''} v${detail.version ?? ''}` : 'Pack'}
        width={860}
        open={!!detail}
        onClose={() => setDetail(null)}
        loading={detailLoading}
        extra={detail && !detailLoading && (
          <Space>
            {editing && detail.status === 'draft' && (
              <>
                <Button size="small" onClick={() => setEntries((prev) => [
                  ...prev,
                  { holiday_date: dayjs().format('YYYY-MM-DD'), name: '', kind: 'declared', half_day: false },
                ])}>
                  Add entry
                </Button>
                <Button size="small" type="primary" loading={saving} onClick={() => void saveDraft()}>Save</Button>
                <Button size="small" loading={saving} disabled={entries.length === 0}
                  onClick={() => void act(async () => { await updateHolidayPackDraft(detail.id, { name: detail.name, entries }); await submitHolidayPack(detail.id); }, 'Submitted for review')}>
                  Submit for review
                </Button>
                <Popconfirm title="Discard this draft?" onConfirm={() => void act(() => discardHolidayPack(detail.id), 'Draft discarded')}>
                  <Button size="small" danger loading={saving}>Discard</Button>
                </Popconfirm>
              </>
            )}
            {detail.status === 'in_review' && canPublish && (
              <>
                <Button size="small" type="primary" loading={saving}
                  onClick={() => { publishForm.setFieldsValue({ effective_from: dayjs(), reason: '' }); setPublishFor(detail); }}>
                  Publish…
                </Button>
                <Button size="small" loading={saving}
                  onClick={() => void act(() => rejectHolidayPack(detail.id), 'Returned to draft')}>
                  Reject
                </Button>
              </>
            )}
          </Space>
        )}
      >
        {detail && !detailLoading && (
          <>
            <Descriptions size="small" column={2} style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Country">{detail.country_code}</Descriptions.Item>
              <Descriptions.Item label="Version">v{detail.version}</Descriptions.Item>
              <Descriptions.Item label="Status">
                <StatusTag {...(() => { const v = statusView(detail.status); return { status: v.status, label: v.label }; })()} />
              </Descriptions.Item>
              <Descriptions.Item label="Source">{detail.source}</Descriptions.Item>
              <Descriptions.Item label="Effective from">{fmtDate(detail.effective_from)}</Descriptions.Item>
              <Descriptions.Item label="Effective to">{detail.effective_to ? fmtDate(detail.effective_to) : 'open'}</Descriptions.Item>
              {detail.change_reason && (
                <Descriptions.Item label="Change reason" span={2}>{detail.change_reason}</Descriptions.Item>
              )}
            </Descriptions>
            <Table
              rowKey={(e) => `${e.holiday_date}:${e.name}`}
              size="small"
              pagination={false}
              dataSource={editing ? entries : (detail.entries ?? [])}
              columns={entryColumns(editing && detail.status === 'draft')}
            />
          </>
        )}
      </Drawer>

      {/* New draft modal */}
      <Modal
        title="New holiday pack draft"
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        confirmLoading={saving}
        onOk={() => {
          void createForm.validateFields().then(async (v) => {
            setSaving(true);
            try {
              const { id } = await createHolidayPackDraft({
                country_code: String(v.country_code).toUpperCase(),
                name: v.name,
                reason: v.reason,
                clone_from_active: !!v.clone_from_active,
              });
              message.success('Draft created');
              setCreateOpen(false);
              await load(false);
              await openDetail(id, true);
            } catch (e: any) {
              apiErr(e, 'Could not create draft');
            } finally {
              setSaving(false);
            }
          });
        }}
      >
        <Form form={createForm} layout="vertical">
          <Form.Item name="country_code" label="Country (ISO-2)" rules={[{ required: true, len: 2 }]}>
            <Input maxLength={2} style={{ textTransform: 'uppercase' }} placeholder="TZ" />
          </Form.Item>
          <Form.Item name="name" label="Pack name" rules={[{ required: true }]}>
            <Input placeholder="Tanzania Public Holidays 2027" />
          </Form.Item>
          <Form.Item name="reason" label="Reason">
            <Input placeholder="e.g. 2027 gazette; Eid correction" />
          </Form.Item>
          <Form.Item name="clone_from_active" valuePropName="checked">
            <Checkbox>Start from the currently published version's entries</Checkbox>
          </Form.Item>
        </Form>
      </Modal>

      {/* Publish modal (maker-checker; mandatory reason; same-day allowed) */}
      <Modal
        title={publishFor ? `Publish ${publishFor.country_code} v${publishFor.version}` : 'Publish'}
        open={!!publishFor}
        onCancel={() => setPublishFor(null)}
        okText="Publish"
        confirmLoading={saving}
        onOk={() => {
          void publishForm.validateFields().then((v) => {
            if (!publishFor) return;
            void act(
              () => publishHolidayPack(publishFor.id, v.effective_from.format('YYYY-MM-DD'), v.reason),
              'Published — tenant calendars will update',
            );
          });
        }}
      >
        <Alert type="warning" showIcon style={{ marginBottom: 12 }}
          message="This updates every subscribed tenant's statutory calendar. The author cannot publish their own pack." />
        <Form form={publishForm} layout="vertical">
          <Form.Item name="effective_from" label="Effective from" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} disabledDate={(d) => d.isBefore(dayjs().startOf('day'))} />
          </Form.Item>
          <Form.Item name="reason" label="Change reason (mandatory)" rules={[{ required: true, min: 5 }]}>
            <Input.TextArea rows={2} placeholder="e.g. Eid al-Adha confirmed by gazette notice №…" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default HolidayPacksPage;
